import { api, getBlobApiErrorMessage } from '../api/client';
import { filenameFromContentDisposition } from './content-disposition';
import { saveBlobAs } from './blob-tab';

/**
 * Download the QuickBooks Online invoice CSV for these invoices (api#799).
 *
 * The name is the API's, read from Content-Disposition (exposed cross-origin
 * since api#799): `<Company>-Invoice-<INV#>.csv` for one, `<Company>-Invoices-
 * <date>.csv` for several. Forced to `.csv` whatever the header says. The
 * fallback is used only when the header is absent or unreadable, and names
 * the invoice number when there is exactly one.
 *
 * Resolves to the saved filename. Rejects with the API's own message — the
 * response is a blob, so an error body has to be read out of it.
 */
export async function downloadInvoiceCsv(
  invoiceIds: string[],
  invoiceNumber?: string | null,
): Promise<string> {
  let res;
  try {
    res = await api.accounting.exportInvoicesCsv(invoiceIds);
  } catch (err) {
    throw new Error(await getBlobApiErrorMessage(err, 'Failed to download the invoice CSV'));
  }
  const fromApi = filenameFromContentDisposition(res.headers?.['content-disposition']);
  const name = fromApi ?? invoiceCsvFallbackName(invoiceIds, invoiceNumber);
  const filename = /\.csv$/i.test(name) ? name : `${name}.csv`;
  saveBlobAs(new Blob([res.data], { type: 'text/csv' }), filename);
  return filename;
}

export function invoiceCsvFallbackName(invoiceIds: string[], invoiceNumber?: string | null): string {
  if (invoiceIds.length === 1 && invoiceNumber) return `Invoice-${invoiceNumber}.csv`;
  return 'Invoices.csv';
}
